import {
  Color,
  Material,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  PerspectiveCamera,
  Vector3,
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { Engine, Entity, registerEvents } from "../../engine/Engine";
import { SugarModel } from "../../loader/SugarModel";
import { SugarModelConfigurator } from "../../loader/SugarModelConfigurator";
import { SugarModelDimensions } from "../../loader/SugarModelDimensions";
import {
  pickIntersectionForSceneSelection,
  raycast,
} from "../../physics/PhysicsUtil";
import { fetchMaterialObjectMap } from "../../util/ThreeUtil";
import {
  CONFIGURATOR_MODEL_TYPE,
  MODEL_PART_TYPE,
  getConfiguratorData,
} from "../../core/configurator/ConfiguratorData";
import { SmoothOrbitController } from "../../core/viewer-runtime/SmoothOrbitController";
import { logger } from "../../util/Logger";
import {
  CallbackConfiguratorCompositionHost,
  type ConfiguratorComposition,
} from "../../core/configurator/ConfiguratorComposition";
import { getConfiguratorComposition } from "../../core/configurator/ConfiguratorCompositionRegistry";
import { ConfiguratorMaterialPort } from "../../core/model/ports/ConfiguratorMaterialPort";
import {
  captureConfiguratorRuntimeDocument,
  type ConfiguratorRuntimeDocument,
} from "./ConfiguratorRuntimeDocument";
import { ConfiguratorRuntimeDocumentLoader } from "./ConfiguratorRuntimeDocumentLoader";
import type { ConfiguratorDragPreviewPort } from "./ConfiguratorDragPreviewController";
import type { DesignerTransformer } from "../../core/drag/DesignerTransformer";
import { logConfiguratorSlotState } from "../../util/logConfiguratorSlotState";
import { Room } from "../../shape/Scene";
import { Wall } from "../../shape/walls/Wall";
import type { Point } from "../../shape/Point";
import type { Line } from "../../shape/Line";
import {
  DESIGNER_CLEAR_SELECTION_COMMAND,
  DESIGNER_SELECT_PRODUCT_COMMAND,
  type DesignerSelectProductDetail,
} from "../../core/events/DesignerEventContracts";

type MaterialConfigEntry = { code: string; material: any };

@registerEvents(
  // Palette HTML5 dragover/drop: <configurator-drag-preview> on viewport.host
  // (DesignMenu pattern — canvas drop is unreliable).
  "model-selected",
  "model-created",
  "showDimensions",
  "showTopView",
)
export class ConfiguratorController extends Entity {
  private _configurators: SugarModel[] = [];

  private _mapMaterialObjects?: Map<Material, Object3D[]>;
  private _selectedModel?: SugarModel;
  private _selectedOldConfigurator?: SugarModelConfigurator;
  private _orbitControls?: OrbitControls;
  private _smoothOrbitControls: SmoothOrbitController;

  private _material: MeshStandardMaterial = new MeshStandardMaterial({
    transparent: true,
    opacity: 0.8,
    color: new Color(0.5, 0.5, 0.5),
    name: "checkMaterial",
  });

  private readonly _defaultPartConfig = new Map<string, MODEL_PART_TYPE>();
  private readonly _defaultSideConfig = new Map<string, MODEL_PART_TYPE>();
  private readonly _defaultMaterialConfig = new Map<
    string,
    MaterialConfigEntry
  >();

  private readonly _configuratorId: string;
  private readonly _room: Room;
  private readonly _dragPreview?: ConfiguratorDragPreviewPort;
  private _runtimeLoading = false;
  private _runtimeCommit?: (label: string) => void;
  private _designerTransformer?: DesignerTransformer;
  private _dropWasPreview = false;
  private _selectionWired = false;

  get configData(): CONFIGURATOR_MODEL_TYPE {
    return getConfiguratorData(this._configuratorId);
  }

  get runtimeLoading(): boolean {
    return this._runtimeLoading;
  }

  /** Product Room — same type RD uses; no walls drawn. */
  get productScene(): Room {
    return this._room;
  }

  captureRuntimeDocument(): ConfiguratorRuntimeDocument {
    return captureConfiguratorRuntimeDocument(
      this._configuratorId,
      this._configurators,
    );
  }

  async loadRuntimeDocument(
    document: ConfiguratorRuntimeDocument,
  ): Promise<void> {
    if (this._runtimeLoading) return;
    this._dragPreview?.cancelAll();
    this._runtimeLoading = true;
    this.selectedModel = undefined;
    const previousRoots = [...this._configurators];
    try {
      const roots = await new ConfiguratorRuntimeDocumentLoader(
        this.configData,
      ).load(document);
      this._configurators = [];
      this.composition?.clear();
      for (const root of previousRoots) {
        this.remove(root);
        this._room.removeModel(root);
        root.dispose();
      }
      const adopt = (model: SugarModel): void => {
        if (!(model instanceof SugarModelConfigurator)) return;
        this.composition?.adopt(model);
        model.models.forEach(adopt);
      };
      for (const root of roots) {
        adopt(root);
        this.__addModel(root);
      }
      this._dimensionsController.update(this._configurators);
      if (this._configurators.length > 0) this.fitCamera();
      this.engine?.dispatchCustomEvent("scene-changed", {
        detail: this.engine.activeScene,
      });
    } finally {
      this._runtimeLoading = false;
    }
  }

  private get _runtimeObjects(): SugarModel[] {
    const roots: SugarModel[] = [];
    const seen = new Set<string>();
    const visit = (model: SugarModel) => {
      if (seen.has(model.uuid)) return;
      seen.add(model.uuid);
      roots.push(model);
      if (model instanceof SugarModelConfigurator) {
        model.models.forEach(visit);
      }
    };
    this._configurators.forEach(visit);
    return roots;
  }

  async whenHistoryStateSettled(): Promise<void> {
    const configurators = this._runtimeObjects.filter(
      (root): root is SugarModelConfigurator =>
        root instanceof SugarModelConfigurator,
    );
    await Promise.all(
      configurators.map((configurator) => configurator.whenStateSettled()),
    );
  }

  setRuntimeCommitHandler(handler: (label: string) => void): void {
    this._runtimeCommit = handler;
  }

  snapshot(label: string): void {
    this._runtimeCommit?.(label);
  }

  constructor(
    orbitControls: OrbitControls,
    configuratorId: string = "default",
    dragPreview?: ConfiguratorDragPreviewPort,
  ) {
    super();
    this._orbitControls = orbitControls;
    this._configuratorId = configuratorId;
    this._dragPreview = dragPreview;
    this._smoothOrbitControls = new SmoothOrbitController(this._orbitControls, {
      constrainFitMaxDistance: false,
    });
    this._room = new Room((start: Point, end: Point) => new Wall(start, end));
    this.name = "SugarConfiguratorControllerTarget";
  }

  get composition(): ConfiguratorComposition | undefined {
    return getConfiguratorComposition(this._room);
  }

  /** Host for Room-backed configurator-bridge (DesignerConfiguratorBridge). */
  createCompositionHost(): CallbackConfiguratorCompositionHost {
    return new CallbackConfiguratorCompositionHost({
      objects: () => this._configurators,
      addRoot: (model) => this.__addModel(model),
      removeRoot: (model) => this.__removeModel(model),
      createConfigurator: () => this._createCompositionConfigurator(),
    });
  }

  bindDesignerTransformer(transformer: DesignerTransformer): void {
    this._designerTransformer = transformer;
    // CFG: piece-only snap — intact roots do not merge with each other.
    transformer.allowConfiguratorRootSnap = false;
    // CFG piece snap radius. Shared default is 0.4.
    transformer.jointSnapDistance = 1.2;
    transformer.setLifecycleHooks({
      beforeGrab: (model) => this.prepareSceneGrab(model),
      beforeCommitDrop: (model) => this.commitSceneDropPreview(model),
      afterDrop: (model) => this.finishSceneDrop(model),
      setOrbitEnabled: (enabled) => this.setOrbitEnabled(enabled),
    });
  }

  unbindDesignerTransformer(transformer: DesignerTransformer): void {
    if (this._designerTransformer === transformer) {
      transformer.setLifecycleHooks(undefined);
      this._designerTransformer = undefined;
    }
  }

  setOrbitEnabled(enabled: boolean): void {
    if (this._orbitControls) this._orbitControls.enabled = enabled;
  }

  /**
   * Legacy CFG grabModel: detach now (not on JointSnap), complete only on drop.
   * JointSnap then sees no configurator and skips a second detach.
   */
  prepareSceneGrab(checkModel: SugarModel): void {
    if (!this._dragPreview?.isPreview(checkModel)) {
      this.setMaterials(checkModel);
    }

    const configurator = checkModel.configurator as
      | SugarModelConfigurator
      | undefined;
    if (configurator) {
      logger.info("Grab configurator", configurator);
      this.composition?.detach(checkModel, false);
      this._selectedOldConfigurator = configurator;
    }

    if (configurator && configurator.models.length < 1) {
      this.__removeModel(configurator);
    }

    this.engine?.activeScene?.attach(checkModel);
    logConfiguratorSlotState("prepareSceneGrab", [
      ...this._configurators,
      checkModel,
    ]);
  }

  async commitSceneDropPreview(model: SugarModel): Promise<boolean> {
    this._dropWasPreview = !!this._dragPreview?.isPreview(model);
    if (
      this._dropWasPreview &&
      !(await this._dragPreview!.commit(model))
    ) {
      return false;
    }
    return true;
  }

  /** Legacy CFG addModel tail: place root if needed, then complete leftover on drop. */
  async finishSceneDrop(model: SugarModel): Promise<void> {
    if (!this._dropWasPreview) this.resetMaterials(model);

    // Bridge may already have attached via joint-snap commit during mover.drop.
    if (
      !(model instanceof SugarModelConfigurator) &&
      !model.configurator
    ) {
      if (model.data?.replaceType == 0) {
        this.composition?.create([model]);
        if (this._selectedOldConfigurator?.models.length == 0) {
          this._selectedOldConfigurator = undefined;
        }
      } else {
        model.removeFromParent();
      }
    } else if (model instanceof SugarModelConfigurator) {
      if (!this._configurators.includes(model)) this.__addModel(model);
      else if (!this.children.includes(model)) this.add(model);
    }

    // Drop confirmed split → remaining neighbors may re-bind.
    this._selectedOldConfigurator?.complete();
    this._selectedOldConfigurator = undefined;
    this.fitCamera();
    this.engine!.dispatchCustomEvent("model-added");
    await this.whenHistoryStateSettled();
    this.snapshot("Place module");
    logConfiguratorSlotState("finishSceneDrop", this._configurators);
  }

  onAdd(engine: Engine): void {
    this.engine?.addEntity(this._smoothOrbitControls);
    this._wireSelectionEvents(engine);
  }

  onRemove(engine: Engine): void {
    this._dragPreview?.cancelAll();
    this.engine?.removeEntity(this._smoothOrbitControls);
    this._unwireSelectionEvents(engine);
  }

  private _wireSelectionEvents(engine: Engine): void {
    if (this._selectionWired) return;
    engine.addEventListener(
      DESIGNER_SELECT_PRODUCT_COMMAND,
      this._onDesignerSelectProduct,
    );
    engine.addEventListener(
      DESIGNER_CLEAR_SELECTION_COMMAND,
      this._onDesignerClearSelection,
    );
    this._selectionWired = true;
  }

  private _unwireSelectionEvents(engine: Engine): void {
    if (!this._selectionWired) return;
    engine.removeEventListener(
      DESIGNER_SELECT_PRODUCT_COMMAND,
      this._onDesignerSelectProduct,
    );
    engine.removeEventListener(
      DESIGNER_CLEAR_SELECTION_COMMAND,
      this._onDesignerClearSelection,
    );
    this._selectionWired = false;
  }

  private readonly _onDesignerSelectProduct = (event: Event): void => {
    const detail = (event as CustomEvent<DesignerSelectProductDetail>).detail;
    const model = detail?.model;
    if (!model) return;
    this.selectedModel = model;
  };

  private readonly _onDesignerClearSelection = (): void => {
    this.selectedModel = undefined;
  };

  get models() {
    return this._configurators;
  }

  get configurators() {
    return this._configurators.map((m) => m as SugarModelConfigurator);
  }

  get isGrabbed() {
    return this._designerTransformer?.mover.active ?? false;
  }

  get selectedModel() {
    return this._selectedModel;
  }

  releaseCancelledPreview(model: SugarModel | undefined): void {
    if (!model) return;
    this._designerTransformer?.abortModelDragForCameraGesture();
    this._selectedOldConfigurator = undefined;
    if (this._orbitControls) this._orbitControls.enabled = true;
  }

  onModelCreatedOccured(e: { detail: SugarModel }): void {
    if (this._runtimeLoading) return;
    const newModel = e.detail;
    this.__addModel(newModel);
    this._designerTransformer?.grabModel(newModel);
  }

  onDragoverOccured(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
    const t = this._designerTransformer;
    if (!t?.selectedModel) return;
    t.moveModel(t.selectedModel, event);
  }

  onDropOccured(event: DragEvent) {
    event.preventDefault();
    // dragend runs after drop; mark committed first so cancelUncommitted is a no-op.
    this._dragPreview?.acknowledgeDrop();
    this._designerTransformer?.dropModel();
  }

  set selectedModel(value: SugarModel | undefined) {
    const oldValue = this._selectedModel;
    this._selectedModel = value;

    if (this._selectedModel && this._selectedModel != oldValue) {
      this.engine!.dispatchCustomEvent("model-selected", {
        detail: this._selectedModel,
      });
    } else if (oldValue && !this._selectedModel) {
      // `_selectedModel` is falsy in this branch, so the old `detail` was
      // always undefined. Emitting without a detail matches the other
      // `model-deselected` producers and clears one contract conflict.
      this.engine!.dispatchCustomEvent("model-deselected");
    }
  }

  checkModel(checkModel: SugarModel, position: Vector3 = new Vector3()) {
    if (checkModel instanceof SugarModelConfigurator) {
      checkModel.position.copy(position);
      return;
    }
    const mover = this._designerTransformer?.mover;
    if (!mover) {
      checkModel.position.copy(position);
      return;
    }
    checkModel.position.copy(mover.resolveFor(checkModel, position));
  }

  addModel(addModel: SugarModel, previewMaterialsManaged = false) {
    if (addModel instanceof SugarModelConfigurator) {
      this.__addModel(addModel);
    } else if (addModel.data?.replaceType == 0) {
      this.composition?.create([addModel]);
    } else {
      addModel.removeFromParent();
    }

    if (!previewMaterialsManaged) this.resetMaterials(addModel);
    this._selectedOldConfigurator?.complete();
    this._selectedOldConfigurator = undefined;
  }

  removeModel(removeModel: SugarModel) {
    this._configurators = this._configurators.filter((e) => e != removeModel);
    this._room.removeModel(removeModel);
    this.remove(removeModel);
    this.engine?.dispatchCustomEvent("scene-changed", {
      detail: this.engine.activeScene,
    });
    this._dimensionsController.update(this._configurators);
  }

  async removeSelected() {
    if (!this._selectedModel) return;
    if (this._selectedModel.configurator) {
      const sugarModelConf = this._selectedModel
        .configurator as SugarModelConfigurator;

      sugarModelConf.removeModel(this._selectedModel);
      this._selectedModel.removeFromParent();

      if (sugarModelConf.models.length < 1) this.removeModel(sugarModelConf);
      else this._dimensionsController.update(this._configurators);
    } else {
      this.removeModel(this._selectedModel);
    }

    this.fitCamera();
    this.selectedModel = undefined;
    await this.whenHistoryStateSettled();
    this.snapshot("Remove module");
  }

  castModel(event: MouseEvent | Touch | TouchEvent) {
    if (1 > this.models.length) return;

    const casts = raycast(event, this.models).filter(
      (e) => !(e.object as any).isLine,
    );
    return pickIntersectionForSceneSelection(casts);
  }

  removeAll() {
    this._configurators.forEach((m) => {
      this._room.removeModel(m);
      this.remove(m);
    });
    this._configurators = [];
  }

  setMainPart(detail: MODEL_PART_TYPE) {
    this._defaultPartConfig.set(detail.type, detail);
  }

  async changeMainPart() {
    const selectedConfigurator = this.getSelectedConfigurator();
    return await selectedConfigurator?.changeMainPart(
      new Map(this._defaultPartConfig),
    );
  }

  setSidePart(config: MODEL_PART_TYPE) {
    this._defaultSideConfig.set(config.type, config);
  }

  async changeSidePart() {
    logger.debug("changeSidePart", this._defaultSideConfig);
    const selectedConfigurator = this.getSelectedConfigurator();
    logger.debug("selectedConfigurator", selectedConfigurator);
    await selectedConfigurator?.changeSidePart(
      new Map(this._defaultSideConfig),
    );
    logger.debug(
      "selectedConfigurator after changeSidePart",
      selectedConfigurator,
    );
    this._dimensionsController.update([selectedConfigurator]);
    logger.debug("dimensionsController updated", this._dimensionsController);
  }

  setMaterial(materialConfig: Map<string, any>) {
    this._defaultMaterialConfig.clear();
    materialConfig
      .keys()
      .forEach((k) =>
        this._defaultMaterialConfig.set(k, materialConfig.get(k)),
      );
  }

  async applyMaterial() {
    const selectedConfigurator = this.getSelectedConfigurator();
    if (!selectedConfigurator) return undefined;
    // Goes through the shared material port so viewer / configurator / room
    // designer all mutate materials via one seam (platform/model-runtime).
    return await new ConfiguratorMaterialPort(selectedConfigurator).applyConfig(
      new Map(this._defaultMaterialConfig),
    );
  }

  private getSelectedConfigurator() {
    if (this._selectedModel instanceof SugarModelConfigurator)
      return this._selectedModel;
    if (this._selectedModel?.configurator)
      return this._selectedModel?.configurator as SugarModelConfigurator;
    return this._configurators[
      this._configurators.length - 1
    ] as SugarModelConfigurator;
  }

  private fitCamera() {
    this.engine?.dispatchCustomEvent("fit-camera", {
      detail: { object: this },
    });
  }

  private setMaterials(checkModel: Object3D) {
    this._mapMaterialObjects = fetchMaterialObjectMap(checkModel);

    checkModel?.traverse((o) => {
      if (o instanceof Mesh) {
        o.material = this._material;
      }
    });
  }

  private resetMaterials(checkModel: Object3D) {
    this._mapMaterialObjects?.keys()?.forEach((key) => {
      this._mapMaterialObjects?.get(key)?.forEach((object) => {
        (object as any).material = key;
      });
    });
    this._mapMaterialObjects?.clear();
  }

  private __addModel(model: SugarModel) {
    if (!this._configurators.includes(model)) this._configurators.push(model);
    if (!this._room.models.includes(model)) this._room.addModel(model);
    // Room is logical (not Object3D). Visual parent = controller under activeScene.
    this.add(model);
  }

  private __removeModel(model: SugarModel) {
    this._configurators = this._configurators.filter((e) => e != model);
    this._room.removeModel(model);
    this.remove(model);
  }

  private _createCompositionConfigurator(): SugarModelConfigurator {
    const configurator = new SugarModelConfigurator(
      new Map(
        this._selectedOldConfigurator?.mainConfig || this._defaultPartConfig,
      ),
      new Map(
        this._selectedOldConfigurator?.sideConfig || this._defaultSideConfig,
      ),
      new Map(
        this._selectedOldConfigurator?.materialConfig ||
          this._defaultMaterialConfig,
      ),
      this.configData,
    );
    configurator.addEventListener("configured" as any, () => {
      this.engine?.dispatchCustomEvent("scene-changed", {
        detail: this.engine.activeScene,
      });
      this._dimensionsController.update(this._configurators);
    });
    return configurator;
  }

  onShowTopViewOccured(e: { detail: any }) {
    const camera = this.engine?.activeCamera?.instance as PerspectiveCamera;
    this._smoothOrbitControls.setOrbitPosition(
      this._orbitControls?.target
        ?.clone()
        .add(
          new Vector3(
            0,
            camera.position.distanceTo(this._orbitControls?.target!),
            0,
          ),
        )!,
      e?.detail,
    );
    this._smoothOrbitControls.setOrbitTarget(
      this._orbitControls?.target!,
      e?.detail,
    );
  }

  private _dimensionsController = new SugarModelDimensions();
  onShowDimensionsOccured(e: {
    detail: boolean | Vector3 | { show?: boolean; outerSize?: Vector3 };
  }) {
    const detail = e?.detail;

    if (detail === true) {
      this._dimensionsController.show(this.models);
    } else if (detail === false) {
      this._dimensionsController.hide();
    } else if (detail instanceof Vector3) {
      this._dimensionsController.show(this.models, true, detail);
    } else if (detail && typeof detail === "object") {
      if (detail.show === false) {
        this._dimensionsController.hide();
      } else {
        this._dimensionsController.show(this.models, true, detail.outerSize);
      }
    } else {
      this._dimensionsController.toggle(this.models);
    }
  }

  onDeleteOccured() {
    this.removeSelected();
  }
}
